import React, { useEffect, useRef, useState } from 'react';
import { isNativeApp } from '../utils/platform';
import { APP_STORE_ID, appStoreUrl, shouldOfferGetTheApp } from '../shared/appStore';
import { isVendorInviteUrl } from '../shared/vendorInviteLink';

const DISMISS_KEY = 'pacarion.getTheApp.dismissed';

function readDismissed() {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch (error) {
    return false;
  }
}

/**
 * Dismissible banner on iOS Safari (and other iOS browsers), including the
 * login screen. Hidden inside the Capacitor app and on desktop.
 */
export default function GetTheAppBanner() {
  const [dismissed, setDismissed] = useState(readDismissed);
  const bannerRef = useRef(null);
  const native = isNativeApp();
  const invitePage = typeof window !== 'undefined' && (
    isVendorInviteUrl(window.location.href)
    || new URLSearchParams(window.location.search).get('preview') === 'vendor-invite'
  );
  const visible = !invitePage && shouldOfferGetTheApp({
    native,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    platform: typeof navigator !== 'undefined' ? navigator.platform : '',
    maxTouchPoints: typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0,
    dismissed,
  });
  const storeUrl = appStoreUrl(APP_STORE_ID);

  useEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.removeAttribute('data-app-banner');
      root.style.removeProperty('--pacarion-app-banner');
      return undefined;
    }
    root.setAttribute('data-app-banner', '1');
    const apply = () => {
      const height = bannerRef.current ? bannerRef.current.offsetHeight : 0;
      if (height > 0) root.style.setProperty('--pacarion-app-banner', `${height}px`);
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      root.removeAttribute('data-app-banner');
      root.style.removeProperty('--pacarion-app-banner');
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || !storeUrl) return undefined;
    const meta = document.createElement('meta');
    meta.name = 'apple-itunes-app';
    meta.content = `app-id=${APP_STORE_ID}`;
    document.head.appendChild(meta);
    return () => {
      if (meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, [visible, storeUrl]);

  if (!visible) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch (error) {
      // The banner still closes for this view.
    }
    setDismissed(true);
  };

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label="Get the Pacarion app"
      style={{
        position: 'relative',
        flex: '0 0 auto',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        boxSizing: 'border-box',
        padding: '0.55rem 0.75rem',
        paddingTop: 'calc(0.55rem + env(safe-area-inset-top, 0px))',
        background: '#0f172a',
        color: '#f9fafb',
      }}
    >
      <p style={{ margin: 0, flex: '1 1 auto', fontSize: '0.95rem', lineHeight: 1.35 }}>
        Get the Pacarion app
      </p>
      {storeUrl ? (
        <a href={storeUrl} style={{ color: '#5eead4', fontWeight: 700, minHeight: '44px', display: 'inline-flex', alignItems: 'center' }}>
          App Store
        </a>
      ) : null}
      <button
        type="button"
        onClick={dismiss}
        style={{
          minHeight: '44px',
          minWidth: '44px',
          background: 'transparent',
          color: '#f9fafb',
          border: '1px solid #94a3b8',
          borderRadius: '8px',
          fontWeight: 650,
        }}
      >
        Dismiss
      </button>
    </div>
  );
}
